import { SceneValidation } from '@provider-directory/models/decision-tree.model';
import { hasOnlyGroupAssociation } from '@provider-directory/models/provider.model';
import {
  ProviderDetailsResource,
  ReasonForVisitResource,
} from '@provider-directory/types/providers.contracts';

type DecisionTreeValidationFunc = (ctx: SceneValidationContext) => boolean;

export interface SceneValidationContext {
  provider?: ProviderDetailsResource | null;
  reasonForVisit?: string | null;
  decisionTreeState?: Record<string, unknown>;
  associationGroupCodes?: Record<string, string[]>;
}

const isRFVSupportedByProvider: DecisionTreeValidationFunc = (
  validationContext,
) => {
  const { provider, reasonForVisit } = validationContext;
  if (!provider || !reasonForVisit) {
    return false;
  }
  const supportedReasonsForVisit = provider.locations.flatMap((location) =>
    location.reasonsForVisit.map((rfv: ReasonForVisitResource) => rfv.id),
  );

  return supportedReasonsForVisit.includes(reasonForVisit);
};

const isAcceptedRVFAllergyWithoutDisclaimer: DecisionTreeValidationFunc = (
  validationContext,
) => {
  const { reasonForVisit } = validationContext;
  if (!reasonForVisit) {
    return true;
  }

  const allergyRFVsWithDisclaimer: string[] = [
    'medication-allergies',
    'food-allergies',
    'general-allergy',
  ];
  return !allergyRFVsWithDisclaimer.includes(reasonForVisit);
};

const isWeightManagementSupportedByHospital: DecisionTreeValidationFunc = ({
  provider,
  associationGroupCodes,
}) => {
  if (!provider) {
    return true;
  }

  return !hasOnlyGroupAssociation(
    provider,
    'montefiore',
    associationGroupCodes,
  );
};

/**
 * PCP Pediatrics: Routes established patients based on age
 * Returns true if patient is under 18 (stays on pediatric error scene)
 * Returns false if patient is 18+ (routes to adult error scene)
 */
const isPcpPediatricsEstablishedPatientPediatric: DecisionTreeValidationFunc = (
  validationContext,
) => {
  const { decisionTreeState } = validationContext;
  const patientAge = decisionTreeState?.['is-adult'];
  return patientAge === 'under18';
};

/**
 * PCP Pediatrics: Routes new patients to age-appropriate RFV selection
 * Returns true if patient is under 18 (stays on pediatric RFV scene)
 * Returns false if patient is 18+ (routes to adult RFV scene)
 */
const isPcpPediatricsNewPatientPediatric: DecisionTreeValidationFunc = (
  validationContext,
) => {
  const { decisionTreeState } = validationContext;
  const patientAge = decisionTreeState?.['is-adult'];
  // If under18, return true (stay on pediatric RFV)
  // If over18, return false (route to adult RFV via failedValidationScene)
  return patientAge === 'under18';
};

const sceneValidationMap: Record<
  SceneValidation['type'],
  DecisionTreeValidationFunc
> = {
  rfvSupportedByProvider: isRFVSupportedByProvider,
  rfvAllergyDisclaimer: isAcceptedRVFAllergyWithoutDisclaimer,
  weightManagementHospitalCheck: isWeightManagementSupportedByHospital,
  pcpPediatricsEstablishedPatientRouting:
    isPcpPediatricsEstablishedPatientPediatric,
  pcpPediatricsNewPatientRouting: isPcpPediatricsNewPatientPediatric,
};

export const getSceneValidationByType = (
  validationType: SceneValidation['type'],
): DecisionTreeValidationFunc => sceneValidationMap[validationType];
